// import

import clsx from 'clsx';
import * as R from 'ramda';
import type {HTMLAttributes, PropsWithChildren, ReactChild} from 'react';
import {useEffect, useCallback, useRef} from 'react';
import type {UseFormReturn} from 'react-hook-form';

import {usePostMessage} from 'modules/MessageList';

import {useSignupSubmit} from './useSignupSubmit';

// types

type Props = HTMLAttributes<HTMLFormElement> & PropsWithChildren<{
  groups?: string[];
  buttonText?: ReactChild;
  inputClass?: string;
  buttonClass?: string;
  form: UseFormReturn;
  parseSubmit?: Parameters<typeof useSignupSubmit>[1];
}>;

// component

export function SignupForm(props: Props) {
  const {form, placeholder = 'author@email.com', groups, children, parseSubmit, buttonText = 'Subscribe', inputClass, buttonClass, ...rest} = props;
  const {formState} = form;
  const formRef = useRef<HTMLFormElement | null>(null);
  const submit = useSignupSubmit(groups, parseSubmit);
  const isValid = !formState.isDirty || R.isEmpty(form.formState.errors);
  const postMessage = usePostMessage();

  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      postMessage({
        color: 'green',
        text: 'Success! Check your inbox to confirm you\'re all set.',
      });
    } else if (!isValid) {
      postMessage({
        color: 'red',
        text: 'Something went wrong. But don\'t worry! Our code monkey has been notified. He\'ll eat the bug and get you signed up manually.',
      });
    }
  }, [formState.isSubmitSuccessful, isValid]);

  const onSubmit = useCallback(async (values) => {
    try {
      form.clearErrors();
      await submit(values);
      formRef.current?.blur();
      form.reset();
    } catch (err) {
      console.error(err.message);
    }
  }, []);

  return (
    <form
      {...rest}
      ref={formRef}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      {children}

      <input
        {...form.register('email', {required: true})}
        required
        className={clsx(inputClass, {invalid: form.formState.errors.email})}
        disabled={formState.isSubmitting}
        placeholder={placeholder}
        type="email"
      />

      <button
        className={buttonClass}
        disabled={formState.isSubmitting}
        type="submit"
      >
        {buttonText}
      </button>
    </form>
  );
}
